import {
  ChamberMemberProMembershipModelPaymentResponsibilityEnum,
  CmpInvoiceBillingSnapshotReturn,
  GetCmpMemberDetailQuery,
} from "../../../graphql/types";
import { InvoicePartySnapshot } from "../Invoicing/mockInvoiceData";
import { EMAIL_FORMAT_MESSAGE } from "../../../utils/fieldMessages";

export const chamberGroupInsurancePaymentMarker =
  "Paid by Johnson Group (Chamber Insurance Plan)";

export type MemberBillingResponsibilityOption =
  | "memberPays"
  | "affiliatedPrimaryCompany"
  | "otherThirdParty"
  | "chamberGroupInsurance"
  | "complimentary"
  | "freeLifetime";

export type MemberThirdPartyBillingSnapshot = InvoicePartySnapshot;

export const emptyMemberThirdPartyBillingSnapshot =
  (): MemberThirdPartyBillingSnapshot => ({
    customerName: "",
    contactName: "",
    contactEmail: "",
    contactPhone: "",
    addressLine1: "",
    addressLine2: "",
    city: "",
    province: "",
    postalCode: "",
    country: "Canada",
  });

export const mapRestartedMemberThirdPartyBillingSnapshot = ({
  paymentResponsibility,
  thirdPartyBillingSnapshot,
}: {
  paymentResponsibility?:
    | ChamberMemberProMembershipModelPaymentResponsibilityEnum
    | null;
  thirdPartyBillingSnapshot?: CmpInvoiceBillingSnapshotReturn | null;
}): MemberThirdPartyBillingSnapshot => {
  if (
    paymentResponsibility !==
      ChamberMemberProMembershipModelPaymentResponsibilityEnum.ThirdPartyPays ||
    !thirdPartyBillingSnapshot
  ) {
    return emptyMemberThirdPartyBillingSnapshot();
  }

  return {
    customerName: thirdPartyBillingSnapshot.customerName || "",
    contactName: thirdPartyBillingSnapshot.contactName || "",
    contactEmail: thirdPartyBillingSnapshot.contactEmail || "",
    contactPhone: thirdPartyBillingSnapshot.contactPhone || "",
    addressLine1: thirdPartyBillingSnapshot.addressLine1 || "",
    addressLine2: thirdPartyBillingSnapshot.addressLine2 || "",
    city: thirdPartyBillingSnapshot.city || "",
    province: thirdPartyBillingSnapshot.province || "",
    postalCode: thirdPartyBillingSnapshot.postalCode || "",
    country: thirdPartyBillingSnapshot.country || "Canada",
  };
};

export const getMemberThirdPartyBillingSnapshotErrors = (
  snapshot: MemberThirdPartyBillingSnapshot
): Partial<Record<keyof MemberThirdPartyBillingSnapshot, string>> => {
  const errors: Partial<
    Record<keyof MemberThirdPartyBillingSnapshot, string>
  > = {};
  const required: Array<{
    field: keyof MemberThirdPartyBillingSnapshot;
    message: string;
  }> = [
    { field: "customerName", message: "Enter the customer or company name." },
    { field: "contactEmail", message: "Enter the billing contact email." },
    // The payer address is optional (approved Oct 12 2026). The backend
    // issues the invoice and leaves missing address fields off it.
  ];

  required.forEach(({ field, message }) => {
    if (!snapshot[field].trim()) {
      errors[field] = message;
    }
  });

  if (
    snapshot.contactEmail.trim() &&
    !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(snapshot.contactEmail.trim())
  ) {
    errors.contactEmail = EMAIL_FORMAT_MESSAGE;
  }

  return errors;
};

// Billing option errors shared by Create Member Step 4 and Update Member Step 5.
// The labels are the radio titles and the Step 3 section title.
export const memberBillingPrimaryCompanyNotSelectedMessage =
  "To use Invoice Affiliated Primary Company, first select the Primary Member Company in Step 3, Member Insights, under Associate Member Company.";

/** `setUpLabel` is the noun-swapped "[Chamber] Set Up" page name. */
export const getMemberBillingGroupInsuranceNotSetUpMessage = (setUpLabel: string): string =>
  `This billing option is not set up yet. Turn on Chamber Group Insurance and enter its billing details in ${setUpLabel} > Global Settings. Or select another billing option.`;

export const memberBillingThirdPartyPayerMissingMessage =
  "The Other Third-Party Payer saved for this member has no name. Select another billing option.";

export const affiliatedPrimaryCompanyPaymentNotes =
  "Invoice affiliated primary company";

/**
 * Design (13) Update Member Step 5: the invoice for an associate member goes
 * to its affiliated Primary Member company ("Attn: Accounts Payable"). The
 * payer is that member's Accounts Payable contact and billing address, or its
 * primary contact and member address when those are empty.
 */
export const getAffiliatedPrimaryCompanyBillingSnapshot = (
  primaryMember?: GetCmpMemberDetailQuery["getCmpMemberDetail"]["member"] | null
): MemberThirdPartyBillingSnapshot | null => {
  if (!primaryMember) return null;
  const extension = primaryMember.cmpExtension;
  const apAddress = extension?.accountsPayableBillingAddress;
  const hasApAddress = Boolean(apAddress?.streetAddress?.trim());
  const address = hasApAddress ? apAddress : primaryMember.address;
  const apEmail = extension?.accountsPayableContactEmail?.trim() || "";

  return {
    customerName: primaryMember.name || "",
    contactName: (apEmail ? extension?.accountsPayableContactName : primaryMember.primaryContactName) || "",
    contactEmail: apEmail || primaryMember.primaryContactEmail || "",
    contactPhone: (apEmail ? extension?.accountsPayableContactNumber : primaryMember.primaryContactPhoneNumber) || "",
    addressLine1: address?.streetAddress || "",
    addressLine2: "",
    city: address?.city || "",
    province: address?.province || "",
    postalCode: address?.postalCode || "",
    country: (hasApAddress ? apAddress?.country : null) || "Canada",
  };
};

/**
 * Fill-only: copies the affiliated Primary Member's billing details into the
 * payer fields staff left empty. Returns null when nothing changes, so typed
 * values are never overwritten.
 */
export const fillEmptyMemberThirdPartyBillingSnapshotFields = (
  current: MemberThirdPartyBillingSnapshot,
  source: MemberThirdPartyBillingSnapshot | null
): MemberThirdPartyBillingSnapshot | null => {
  if (!source) return null;
  const next = { ...current };
  let changed = false;

  (Object.keys(next) as (keyof MemberThirdPartyBillingSnapshot)[]).forEach(
    (field) => {
      const sourceValue = source[field];

      if (
        typeof sourceValue === "string" &&
        sourceValue.trim() &&
        !String(current[field] || "").trim()
      ) {
        (next as Record<string, unknown>)[field] = sourceValue;
        changed = true;
      }
    }
  );

  return changed ? next : null;
};

export const getMemberThirdPartyBillingFields = ({
  affiliatedPrimaryCompanyName,
  billingResponsibility,
  configuredInsurancePayerName,
  savedThirdPartyPayerName,
  savedThirdPartyPaymentNotes,
}: {
  affiliatedPrimaryCompanyName?: string | null;
  billingResponsibility: MemberBillingResponsibilityOption;
  configuredInsurancePayerName?: string | null;
  savedThirdPartyPayerName?: string | null;
  savedThirdPartyPaymentNotes?: string | null;
}) => {
  if (billingResponsibility === "chamberGroupInsurance") {
    return {
      thirdPartyPayerName:
        configuredInsurancePayerName?.trim() ||
        chamberGroupInsurancePaymentMarker,
      thirdPartyPaymentNotes: chamberGroupInsurancePaymentMarker,
    };
  }

  if (billingResponsibility === "affiliatedPrimaryCompany") {
    return {
      thirdPartyPayerName: affiliatedPrimaryCompanyName?.trim() || "",
      thirdPartyPaymentNotes: affiliatedPrimaryCompanyPaymentNotes,
    };
  }

  if (billingResponsibility === "otherThirdParty") {
    return {
      thirdPartyPayerName: savedThirdPartyPayerName?.trim() || "",
      thirdPartyPaymentNotes: savedThirdPartyPaymentNotes?.trim() || "",
    };
  }

  return {
    thirdPartyPayerName: "",
    thirdPartyPaymentNotes: "",
  };
};

const normalizeCompanyName = (value?: string | null) =>
  (value || "")
    .trim()
    .toLocaleLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

export const getMemberBillingResponsibility = ({
  configuredInsurancePayerName,
  insuranceOptionAvailable,
  membership,
}: {
  configuredInsurancePayerName?: string | null;
  insuranceOptionAvailable: boolean;
  membership?: {
    paymentResponsibility?:
      | ChamberMemberProMembershipModelPaymentResponsibilityEnum
      | null;
    thirdPartyPayerName?: string | null;
    thirdPartyPaymentNotes?: string | null;
    isFreeLifetime?: boolean | null;
  } | null;
}): MemberBillingResponsibilityOption => {
  if (
    membership?.paymentResponsibility ===
    ChamberMemberProMembershipModelPaymentResponsibilityEnum.NoCharge
  ) {
    // Records saved before Free Lifetime had its own flag read as Complimentary.
    return membership.isFreeLifetime ? "freeLifetime" : "complimentary";
  }

  if (
    membership?.paymentResponsibility !==
    ChamberMemberProMembershipModelPaymentResponsibilityEnum.ThirdPartyPays
  ) {
    return "memberPays";
  }

  // A saved affiliated choice keeps its own option when the member reopens.
  if (
    membership.thirdPartyPaymentNotes?.trim() ===
    affiliatedPrimaryCompanyPaymentNotes
  ) {
    return "affiliatedPrimaryCompany";
  }

  if (!insuranceOptionAvailable) {
    return "otherThirdParty";
  }

  const configuredPayer = normalizeCompanyName(configuredInsurancePayerName);
  const savedPayer = normalizeCompanyName(membership.thirdPartyPayerName);
  const hasConfiguredPayerMatch = Boolean(
    configuredPayer && savedPayer === configuredPayer
  );
  const hasExactLegacyMarker =
    membership.thirdPartyPayerName === chamberGroupInsurancePaymentMarker ||
    membership.thirdPartyPaymentNotes === chamberGroupInsurancePaymentMarker;

  return hasConfiguredPayerMatch || hasExactLegacyMarker
    ? "chamberGroupInsurance"
    : "otherThirdParty";
};

/**
 * Claude Design (13) Update Member Form "No Affiliated Member Selected". A
 * member name that already ends with "." (e.g. "Canvas Art Co.") gets no
 * second period.
 */
const withSentenceEnd = (name: string): string =>
  `${name}${/[.!?]$/.test(name.trim()) ? "" : "."}`;

export const getNoAffiliatedMemberMessage = (memberName: string): string =>
  `No member has been selected as an affiliated member for ${withSentenceEnd(memberName)} To invoice an affiliated Primary Member company, select it in Step 3 Member Insight first.`;

/** Design "Invoice Affiliated Primary Company" confirm text, one period after the member name. */
export const getAffiliatedPrimaryCompanyConfirmMessage = ({
  memberName,
  primaryCompanyName,
}: {
  memberName: string;
  primaryCompanyName: string;
}): string =>
  `${primaryCompanyName} is the affiliated Primary Member company for ${withSentenceEnd(memberName)} Please confirm that ${primaryCompanyName} will receive the membership invoice for payment.`;
